import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import PageTransition from '../components/PageTransition';
import HomePage from './HomePage';
import PTOPage from './PTOPage';
import AdminPanel from './AdminPanel';
import ManagerPanel from './ManagerPanel';
import OrgChartPage from './OrgChartPage';
import OnboardingPage from './OnboardingPage';
import ReferralsPage from './ReferralsPage';
import TeamTimeOffPage from './TeamTimeOffPage';
import InviteVisitorPage from './InviteVisitorPage';


const SidebarIcon = ({ name, size = 18 }) => {
  const icons = {
    home: <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>,
    'clipboard-check': <><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" fill="none" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="3" width="6" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M9 14l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></>,
    'calendar-users': <><rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="15" r="2" fill="none" stroke="currentColor" strokeWidth="1.5"/></>,
    'user-plus': <><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" fill="none" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></>,
    visitor: <><path d="M4 21V5a1 1 0 011-1h9a1 1 0 011 1v16" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M15 21h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="11" r="0.9" fill="currentColor"/></>,
    sitemap: <><rect x="9" y="2" width="6" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/><rect x="2" y="18" width="6" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/><rect x="16" y="18" width="6" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M12 6v4m0 0H5v8m7-8h7v8" stroke="currentColor" strokeWidth="1.5"/></>,
    users: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" fill="none" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" fill="none" stroke="currentColor" strokeWidth="1.5"/></>,
    settings: <><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" fill="none" stroke="currentColor" strokeWidth="1.5"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      {icons[name] || null}
    </svg>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeMenuItem, setActiveMenuItem] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [authVerified, setAuthVerified] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const dropdownRef = useRef(null);
  const sidebarRef = useRef(null);

  // Verify auth state is ready before rendering dashboard content
  useEffect(() => {
    const verifyAuth = () => {
      // Since App component now handles auth sync, we can simplify this
      if (!user) {
        // If no user is passed, redirect to login
        navigate('/');
      } else {
        // User data is available, proceed normally
        setAuthVerified(true);
      }
    };

    verifyAuth();
  }, [user, navigate]);

  // Update currentUser when user prop changes
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Check for manager status on mount and page refresh
  useEffect(() => {
    const checkManagerStatus = async () => {
      if (!authVerified || !user) return;
      
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const response = await fetch(`${API_BASE}/api/user/check-manager-status`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasDirectReports !== user.hasDirectReports) {
            // Update the user object with fresh manager status
            const updatedUser = { ...user, hasDirectReports: data.hasDirectReports };
            setCurrentUser(updatedUser);
            
            // Update localStorage with the new status
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const parsedUser = JSON.parse(storedUser);
              parsedUser.hasDirectReports = data.hasDirectReports;
              localStorage.setItem('user', JSON.stringify(parsedUser));
            }
            
            console.log(`Manager status updated for ${user.email}: hasDirectReports=${data.hasDirectReports}`);
          }
        }
      } catch (error) {
        console.error('Error checking manager status:', error);
      }
    };

    checkManagerStatus();
  }, [authVerified, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Don't close if clicking on the hamburger button itself
        const hamburgerBtn = document.querySelector('.hamburger-btn');
        if (hamburgerBtn && hamburgerBtn.contains(event.target)) {
          return;
        }
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [sidebarOpen]);

  // Default to home tab, but allow deep links
  useEffect(() => {
    const syncHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        if (hash === 'manager-action-required') {
          setActiveMenuItem('manager');
        } else {
          setActiveMenuItem(hash);
        }
      } else {
        setActiveMenuItem('home');
        window.location.hash = 'home';
      }
    };
    syncHash();
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  // Use currentUser which has updated manager status
  const effectiveUser = currentUser || user;



  // Build sidebar items dynamically; include Manager/Admin as appropriate
  const menuItems = useMemo(() => {
    const items = [
      { id: 'home', label: 'Home', icon: 'home' },
      { id: 'onboarding', label: 'Onboarding', icon: 'clipboard-check' },
      { id: 'pto', label: 'Time Off', icon: 'calendar' },
      { id: 'team-time-off', label: 'Team Time Off', icon: 'calendar-users' },
      { id: 'referrals', label: 'Referrals', icon: 'user-plus' },
      { id: 'invite-visitor', label: 'Invite Visitor', icon: 'visitor' },
      { id: 'org', label: 'Org Chart', icon: 'sitemap' }
    ];

    // Only show My Team if user is admin or has direct reports (manager)
    if (effectiveUser?.is_admin || effectiveUser?.hasDirectReports) {
      items.push({ id: 'manager', label: 'My Team', icon: 'users' });
    }

    if (effectiveUser?.is_admin) {
      items.push({ id: 'admin', label: 'Admin', icon: 'settings' });
    }
    return items;
  }, [effectiveUser, currentUser]);

  // Show loading state while verifying auth
  if (!authVerified) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  // Handle returning to admin view
  const handleReturnToAdmin = () => {
    // Clear all localStorage data and log out
    localStorage.removeItem('user');
    localStorage.removeItem('originalAdminUser');
    
    // Use the logout function to properly handle the logout process
    handleLogout();
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const handleMenuClick = (itemId) => {
    setActiveMenuItem(itemId);
    // update hash so deep links work and page view logging can detect the change
    window.location.hash = itemId;
    setSidebarOpen(false);
  };



  const renderContent = () => {
    const content = (() => {
      switch (activeMenuItem) {
        case 'home':
          return <HomePage user={effectiveUser} />;
        case 'pto':
          return <PTOPage user={effectiveUser} />;
        case 'team-time-off':
          return <TeamTimeOffPage user={effectiveUser} />;
        case 'onboarding':
          return <OnboardingPage user={effectiveUser} />;
        case 'referrals':
          return <ReferralsPage user={effectiveUser} />;
        case 'invite-visitor':
          return <InviteVisitorPage user={effectiveUser} />;
        case 'admin':
          return effectiveUser?.is_admin ? <AdminPanel user={effectiveUser} /> : <div>Not authorized</div>;
        case 'manager':
          return <ManagerPanel user={effectiveUser} />;
        case 'org':
          return <OrgChartPage user={effectiveUser} />;
        default:
          return <HomePage user={effectiveUser} />;
      }
    })();

    return (
      <PageTransition transitionKey={activeMenuItem}>
        {content}
      </PageTransition>
    );
  };



  return (
    <div className="dashboard-container">
      {/* Top Header */}
      <header className="dashboard-header">
        <div className="header-left">
          {/* Hamburger for mobile - moved to far left */}
          <button className="hamburger-btn" onClick={()=>setSidebarOpen(!sidebarOpen)}>
            &equiv;
          </button>
          <img src="/logo.svg" alt="Symbolica" style={{ height: 18 }} />
          <div style={{ width: 1, height: 24, background: 'var(--symbolica-warm-border)' }} />
          <span className="company-name">The Landing</span>
          
          {/* Return to Admin View button when viewing as employee */}
          {user?.is_view_as && (
            <button 
              onClick={handleReturnToAdmin}
              style={{
                marginLeft: '1rem',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Return to your admin account"
            >
              Return to Admin View
            </button>
          )}
        </div>
        <div className="header-right">
          {user && (
            <div className="user-menu" ref={dropdownRef}>
              <div 
                className="user-summary" 
                style={{ display:'flex', alignItems:'center', gap:'8px', cursor: 'pointer' }}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {user.picture && (
                  <img 
                    src={user.picture}
                    alt={user.name}
                    style={{ width:'32px', height:'32px', borderRadius:'50%', border:'2px solid #e5e7eb' }}
                  />
                )}
                <span style={{ fontSize:'14px', color:'var(--symbolica-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'150px', display:'inline-block', verticalAlign:'middle' }}>
                  {user.name || user.email}
                  {user?.is_view_as && (
                    <span style={{ fontSize:'12px', color:'#dc2626', marginLeft:'4px', fontWeight: '500' }}>
                      (Viewing as Employee)
                    </span>
                  )}
                </span>
                <span style={{ fontSize:'12px', color:'var(--symbolica-muted)' }}>▼</span>
              </div>
              
              {dropdownOpen && (
                <div className="logout-dropdown" style={{ display: 'block', position: 'absolute', right: 0, top: '100%', marginTop: '4px' }}>
                  {user?.is_view_as && (
                    <>
                      <div style={{ 
                        borderBottom: '1px solid #e5e7eb', 
                        paddingBottom: '8px', 
                        marginBottom: '8px',
                        fontSize: '12px',
                        color: '#dc2626',
                        fontWeight: '500'
                      }}>
                        <div>Viewing as:</div>
                        <div style={{ marginTop: '2px', color: 'var(--symbolica-black)' }}>
                          {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--symbolica-muted)' }}>
                          Original Admin: {user.original_admin_email}
                        </div>
                      </div>
                      <button 
                        onClick={handleReturnToAdmin}
                        style={{ 
                          display: 'block', 
                          width: '100%', 
                          textAlign: 'left', 
                          padding: '6px 0', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#dc2626',
                          fontWeight: '500'
                        }}
                      >
                        Return to Admin View
                      </button>
                      <div style={{ borderBottom: '1px solid #e5e7eb', margin: '8px 0' }}></div>
                    </>
                  )}

                  <button 
                    onClick={handleLogout}
                    style={{ 
                      display: 'block', 
                      width: '100%', 
                      textAlign: 'left', 
                      padding: '4px 0', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div className="dashboard-main">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`} ref={sidebarRef}>
          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <button
                key={item.id}
                className={`sidebar-item ${activeMenuItem === item.id ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.id)}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <span className="sidebar-icon"><SidebarIcon name={item.icon} /></span>
                <span className="sidebar-label">{item.label}</span>
              </button>
            ))}
          </nav>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </aside>

        {/* Content Area */}
        <main className="dashboard-content">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard; 